<template>
  <div>
    <operation-report-show-body-form-item-image
      v-if="isReady"
      v-bind="formData"
      @download="onDownload"
      @delete="onDelete"
      @upload="uploadTempImage"
      @change="onChange"
      @show-dialog="showUploadDialog"
      @close-dialog="closeUploadDialog"
    />
  </div>
</template>

<script>
import isString from "lodash/isString";
import OperationReportShowBodyFormItemImage from "./FormItemImage";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import {
  operationLoadingStart,
  operationLoadingClose,
  createStorageFilePath,
  getDataURLFromStoragePath,
  downloadImage,
  createOperationHash,
  notifyDownloadImageFailed,
  notifyUploadTempImageSuccess,
  notifyUploadTempImageFailed,
  calcDefaultValue,
  calcNextFormItemValue,
} from "@/utils/operations";
import AuthService from "@/services/authService";
import mapKeys from "lodash/mapKeys";
import isEmpty from "lodash/isEmpty";
import { createNamespacedHelpers } from "vuex";
const { mapActions } = createNamespacedHelpers("operationReport");

import { ColorMixin } from "@/mixins";
const { AISEA_COLOR } = ColorMixin.data();

const FALLBACK_VALUE = "ー";

// 以下に対応したコンポーネント
//   INPUT_TYPES.SINGLE_IMAGE_FILE, // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード)
export default {
  name: "FormItemImage",
  components: { OperationReportShowBodyFormItemImage },
  props: {
    reportId: {
      type: String,
      required: true,
    },

    // format
    enterpriseFormVersionItem: {
      type: Object,
      required: true,
    },

    reportFormVersion: {
      type: Object,
      required: true,
    },

    // 実データ
    reportFormVersionItem: {
      type: Object,
      required: true,
    },

    fallbackValue: {
      type: String,
      default: FALLBACK_VALUE,
    },

    // 編集権限
    updatable: {
      type: Boolean,
      default: false,
    },

    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },
    // formの順番
    // フォームの値の更新に必要
    formVersionSortOrder: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isReady: false,
      enterpriseId: "",
      imageAcquisitionResults: [], // 画像表示用のデータ取得の結果
      isActiveUploadDialog: false, // 画像ファイルアップロードダイアログ表示
    };
  },

  watch: {
    filePaths: {
      async handler() {
        if (!this.hasEnterpriseId) return;

        this.imageAcquisitionResults = await Promise.allSettled(
          this.filePaths.map(async filePath => {
            const { path, hash, tmpURL } = filePath;
            try {
              const dataURL = tmpURL
                ? tmpURL // 一時ファイル表示用のdataURL
                : await getDataURLFromStoragePath(path);
              return {
                hash,
                url: dataURL,
                success: true,
              };
            } catch {
              return {
                hash,
                url: "",
                success: false, // NOTE: データを取得できなかった場合も要素を使用したいので、全てresolveしてsuccessのフラグで判定
              };
            }
          })
        );
      },
      immediate: true,
      deep: true,
    },
  },

  computed: {
    name() {
      const {
        reportFormVersion,
        reportFormVersionItem,
        enterpriseFormVersionItem,
        updatable,
      } = this;
      const stateUpdatable = updatable ? "enable" : "disable";
      return `operation-report-form-${stateUpdatable}_${reportFormVersion.id}_${reportFormVersionItem.id}_${enterpriseFormVersionItem.id}`; // validationに使用
    },

    imageAcquisitionResultMap() {
      return mapKeys(
        this.imageAcquisitionResults.map(result => result.value),
        "hash"
      ); // {[key: hash]: {hash: string; url: string; success: boolean;}}
    },
    imageUrls() {
      // 要素のsuccessがtrueなら画像URL取得成功、falseなら失敗
      return this.filePaths.map(filePath => {
        const { hash } = filePath;
        const result = this.imageAcquisitionResultMap[hash];
        const loading = result === undefined; // NOTE: undefinedの場合はローディング中

        return {
          ...filePath,
          ...result, // {hash: string; url: string; success: boolean;}
          loading, // boolean
        };
      });
    },
    hasEnterpriseId() {
      return isString(this.enterpriseId) && this.enterpriseId.length > 0; // boolean
    },
    formDataForDisplay() {
      const {
        reportId,
        label,
        required,
        imageUrls: value,
        nullableAndFallbackValue,
        isEditing,
        name,
        nullableLabel,
        isActiveUploadDialog,
        disabled,
        isNullableChecked,
      } = this;
      return {
        reportId,
        label,
        required,
        value,
        fallbackValue: nullableAndFallbackValue,
        isEditing,
        name,
        nullableLabel,
        isActiveUploadDialog,
        disabled,
        isNullableChecked,
      };
    },
    disabled() {
      return !this.updatable;
    },
    formDataForEdit() {
      const { formDataForDisplay, defaultValue } = this;
      return {
        ...formDataForDisplay,
        defaultValue,
      };
    },
    formData() {
      const { formDataForDisplay, formDataForEdit, isEditing } = this;
      if (!isEditing) return formDataForDisplay;
      return formDataForEdit;
    },
    isEdited() {
      return !isEmpty(this.reportFormVersionItem); // レポートのフォームの値がない時は、これまで編集していない
    },
    defaultValue() {
      const { enterpriseFormVersionItem, isEdited, disabled, isEditing } = this;
      if (disabled) return null; // 非活性ならデフォルト値を使用しない
      if (isEdited) return null; // すでに編集ずみの場合は、デフォルト値を使用しない
      if (!isEditing) return null; // 編集中でない場合は、デフォルト値を使用しない
      const defaultValue = calcDefaultValue(this, enterpriseFormVersionItem);
      return defaultValue.map(valueFile => {
        return {
          ...valueFile,
          isDefault: true, // デフォルト値の場合画像パスを作成方法が異なるため、フラグで見分ける
        };
      });
    },
    label() {
      const { enterpriseFormVersionItem } = this;
      return enterpriseFormService.getLabelFromEnterpriseFormVersionItem(
        enterpriseFormVersionItem
      );
    },
    required() {
      return enterpriseFormService.getRequiredFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      );
    },
    filePaths() {
      const {
        valueFiles,
        calcFilePaths,
        calcFilePathsForDefaultValue,
        reportId,
      } = this;
      return valueFiles.map(valueFile => {
        const { isDefault } = valueFile;
        const path = isDefault
          ? calcFilePathsForDefaultValue(valueFile)
          : calcFilePaths(valueFile);
        return {
          path,
          hash: createOperationHash(`${reportId}-${path}`), // 画像を一意に特定鵜するhashを作成
          ...valueFile,
        };
      });
    },
    isNullableChecked() {
      return operationReportService.isNullableChecked(
        this.reportFormVersionItem
      ); // boolean
    },
    nullableLabel() {
      return enterpriseFormService.getNullableLabelFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      ); // string
    },
    formVersionItemId() {
      return this.enterpriseFormVersionItem.id; // string
    },
    hasImage() {
      return operationReportService.hasReportFormVersionItemValue(
        this.reportFormVersionItem
      ); // boolean
    },
    valueFiles() {
      const { hasImage, calcFormValue, reportFormVersionItem } = this;
      if (!hasImage) return [];
      return calcFormValue(reportFormVersionItem); // {content_type: string; name: string; token: string; uploaded_at: string}[]
    },
    nullableAndFallbackValue() {
      if (this.isNullableChecked) return this.nullableLabel;
      return this.fallbackValue;
    },
  },

  methods: {
    ...mapActions({
      setFormVersionItemValue: "setFormVersionItemValue",
      uploadTempFilesOfCurrentFile: "uploadTempFilesOfCurrentFile",
    }),
    calcFormValue(reportFormVersionItem) {
      if (this.isNullableChecked) return this.nullableLabel;
      if (!this.hasImage) return this.fallbackValue;
      return operationReportService.getValueFromReportFormVersionItem(
        reportFormVersionItem
      );
    },

    // ストレージのファイルパスを作成
    calcFilePaths(valueFile) {
      const { enterpriseId, reportId, formVersionItemId } = this;
      const { name } = valueFile;
      return createStorageFilePath(
        enterpriseId,
        reportId,
        formVersionItemId,
        name
      ); // : string
    },

    // 値がデフォルトの場合のストレージのファイルパスを作成
    calcFilePathsForDefaultValue(valueFile) {
      const { enterpriseId, reportId, enterpriseFormVersionItem } = this;
      const formVersionItemId = enterpriseFormService.getDefaultValueId(
        enterpriseFormVersionItem
      );
      const { name } = valueFile;
      return createStorageFilePath(
        enterpriseId,
        reportId,
        formVersionItemId,
        name
      ); // : string
    },

    async onDownload(
      image // {url: string; hash: string;}
    ) {
      try {
        operationLoadingStart(this);
        await downloadImage(image);
      } catch {
        notifyDownloadImageFailed(this);
      } finally {
        operationLoadingClose(this);
      }
    },

    onDelete() {
      const { formVersionSortOrder, reportFormVersionItem } = this;
      const payload = {
        formVersionSortOrder,
        ...reportFormVersionItem,
        value_files: [], // storeから画像を削除 -> value_filesを[]にセットする
      };

      this.setFormVersionItemValue(payload);
    },

    async uploadTempImage(
      documents //
      // {
      //     "name": string;
      //     "size": number;
      //     "type": string; like "image/png";
      //     "src": string; like "data:image/png;base64,iVBO..."
      //     "content": File; // uploadに使用する
      // }[]
    ) {
      try {
        this.$vs.loading({ color: AISEA_COLOR.BLUE });
        const { src: tmpURL, content } = documents[0];
        const {
          formVersionItemId,
          formVersionSortOrder,
          reportFormVersionItem,
          enterpriseFormVersionItem,
        } = this;

        const formData = new FormData();
        formData.append("file", content);

        const result = await this.uploadTempFilesOfCurrentFile({
          formData,
          formVersionItemId,
        });
        if (!result.status) {
          throw new Error(result.message);
        }
        notifyUploadTempImageSuccess(this);
        const nextFormItemValue = calcNextFormItemValue(
          enterpriseFormVersionItem,
          reportFormVersionItem,
          {
            value: [
              {
                ...result.data,
                tmpURL, // 一時ファイル表示用のdataURL
              },
            ],
          }
        );
        const payload = {
          formVersionSortOrder,
          ...nextFormItemValue,
          // value_files: [result.data], // storeから画像を削除 -> value_filesを[]にセットする
        };

        this.setFormVersionItemValue(payload);
        this.closeUploadDialog();
      } catch {
        notifyUploadTempImageFailed(this);
      } finally {
        this.$nextTick(() => {
          this.$vs.loading.close();
        });
      }
    },

    async setEnterpriseId() {
      const user = await AuthService.getUser();
      this.enterpriseId = user.id;
    },

    async setup() {
      await this.setEnterpriseId();
      return true;
    },

    onChange(
      value // { value: [{ "name": string; }], nullableChecked }
    ) {
      const {
        formVersionSortOrder,
        enterpriseFormVersionItem,
        reportFormVersionItem,
      } = this;
      const nextFormItemValue = calcNextFormItemValue(
        enterpriseFormVersionItem,
        reportFormVersionItem,
        value
      );

      const payload = {
        formVersionSortOrder,
        ...nextFormItemValue,
      };

      this.setFormVersionItemValue(payload);
    },
    showUploadDialog() {
      this.isActiveUploadDialog = true;
    },
    closeUploadDialog() {
      setTimeout(() => {
        this.isActiveUploadDialog = false;
      });
    },
  },
  async created() {
    this.isReady = await this.setup();
  },
};
</script>
